import { type Cell, sanitizeParsedNodeAttrs } from "@moment/prosemirror-serialization";
import { createCellID } from "@moment/prosemirror-utils";
import { type LoggerInterface, noopLogger } from "@moment/utils";

export * from "./activeUser";
export * from "./appConfig";
export * from "./auth";
export * from "./canvasSpecifier";
export * from "./documentMetadata";
export * from "./httpRequestPage";
export * from "./idempotentInitRoom";
export * from "./logging";
export * from "./messageActiveUsersBroadcast";
export * from "./messageTransactionBroadcast";
export * from "./pageMetadata";
export * from "./persistenceError";
export * from "./pingpong";
export * from "./presence";
export * from "./prosemirrorClientId";
export * from "./stepsSince";
export * from "./transaction";
export * from "./workspace";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractNodeAttrs = (cell: any, logger: LoggerInterface = noopLogger): Cell["nodeAttrs"] => {
	if (cell.nodeAttrs === null) {
		return {};
	} else if (typeof cell.nodeAttrs === "object") {
		return cell.nodeAttrs;
	}

	try {
		const parsed = JSON.parse(cell.nodeAttrs);
		if (typeof parsed === "object") {
			return sanitizeParsedNodeAttrs(parsed);
		}
		logger.warn(
			"cell nodeAttrs are escaped or stringified. returning an empty object instead.",
			{
				rawNodeAttrs: cell.nodeAttrs,
			}
		);
		return {};
	} catch (e) {
		logger.error("Failed to parse nodeAttrs", { error: e });
		return {};
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fromGQLCell = (cell: any, logger: LoggerInterface = noopLogger): Cell => {
	const nodeAttrs = extractNodeAttrs(cell, logger);
	switch (cell.type) {
		case "JavaScriptFunctionBodyCell":
		case "ProseMirrorJSONCell": {
			return {
				...cell,
				type: cell.type,
				cellName: cell.cellName ?? undefined,
				id: cell.id || createCellID(),
				inspectorInitiallyExpanded: false,
				nodeAttrs,
			};
		}
		default:
			return {
				...cell,
				type: "JavaScriptFunctionBodyCell",
				code: cell.code,
				cellName: cell.cellName ?? undefined,
				id: cell.id || createCellID(),
				inspectorInitiallyExpanded: false,
				nodeAttrs,
			};
	}
};

export declare type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export declare type JsonObject = { [key: string]: Json };

export function serializeForStruct(json: Json): Json {
	if (typeof json !== "object" || json === null) return json;
	if (Array.isArray(json)) return json.map((j) => serializeForStruct(j));
	return Object.fromEntries(
		Object.entries(json)
			.filter(([key, value]) => value !== undefined)
			.map(([key, value]) => [key, serializeForStruct(value)])
	);
}
